'use client';

import React from 'react';
import { PriceVariantFragment } from '@/integrations/crystallize/__gql-codegen-types/catalogue/graphql';
import { FormatCurrency } from '@/ui/hooks/useCurrencyFormatter';

interface CtaPriceProps {
  priceVariant: PriceVariantFragment | null | undefined;
  formatCurrency: FormatCurrency;
}

interface ShouldShowCtaPriceParams {
  pricePosition: string | null | undefined;
  priceVariant: { price?: number | null } | null | undefined;
  hasExternalLink: boolean;
  isRestrictedFallback: boolean;
}

const PRICE_POSITIONS = new Set(['left', 'right']);

export function shouldShowCtaPrice({
  pricePosition,
  priceVariant,
  hasExternalLink,
  isRestrictedFallback,
}: ShouldShowCtaPriceParams): boolean {
  if (!pricePosition || !PRICE_POSITIONS.has(pricePosition)) {
    return false;
  }

  // A variant without a price for the visitor's area would otherwise render the
  // 'Missing price' placeholder next to the button.
  if (priceVariant?.price == null) {
    return false;
  }

  // An external link replaces the product as the button's destination, so the
  // product's price would describe a page the button does not open.
  if (hasExternalLink) {
    return false;
  }

  // A visitor rerouted to the fallback must not see the price of a page they cannot open.
  return !isRestrictedFallback;
}

export function CtaPrice({ priceVariant, formatCurrency }: Readonly<CtaPriceProps>) {
  return (
    <span className="block text-md font-bold">
      {formatCurrency(priceVariant, { maximumFractionDigits: 0, minimumFractionDigits: 0 })}
    </span>
  );
}
